/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { getAxios } from '@/lib/axios';
import type { AxiosInstance } from 'axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import type { QueryClient } from '@tanstack/react-query';
import type { YearsTableDataResponse } from '../interface';
import environmentConfig from '@/config/environment';

type Options = {
  hasAssociatedPartType?: boolean;
  make?: string;
  model?: string;
  productId?: string | null;
};

type QueryOptions = Options & {
  country: string;
  customerType: string;
};

type MappedYearsTableData = YearsTableDataResponse;

const YEARS_TABLE_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/years-table-driven`;

const getYearsTableData = async (
  options: QueryOptions,
  axiosInstance?: AxiosInstance,
): Promise<MappedYearsTableData> => {
  const { country, customerType, hasAssociatedPartType, make, model, productId } = options;
  const params = {
    country,
    customerType,
    hasAssociatedPartTypes: hasAssociatedPartType,
    make,
    model,
    productId,
  };
  const response = await getAxios(axiosInstance).get<YearsTableDataResponse>(YEARS_TABLE_URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

const selector = (data: YearsTableDataResponse): MappedYearsTableData => {
  data.values = data.values.sort((a, b) => Number(b.year) - Number(a.year));
  return data;
};

const { useData: useYearsTableData, prefetch: prefetchYearsTableData } = createQuery<
  MappedYearsTableData,
  QueryOptions
>('years-table', async (options, axiosInstance) => getYearsTableData(options, axiosInstance));

export const useYearsTable = ({ ...options }: Options) => {
  const locale = useLocale();
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';

  return useYearsTableData({
    enabled: !!options.productId && !!options.model && !!options.make,
    country,
    customerType,
    ...options,
  });
};

export const prefetchYearsTable = async (
  queryClient: QueryClient,
  options: QueryOptions,
  axiosInstance: AxiosInstance,
) => {
  return prefetchYearsTableData(queryClient, options, axiosInstance);
};
